
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Review } from '@/types/review';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface AddResponseDialogProps {
  review: Review;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddResponseDialog({ review, open, onOpenChange }: AddResponseDialogProps) {
  const queryClient = useQueryClient();
  const [response, setResponse] = useState(review.admin_response || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!response.trim()) {
      toast.error('Response cannot be empty');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.rpc('add_review_response', {
        p_review_id: review.id,
        p_response: response
      });
      
      if (error) throw error;
      
      toast.success('Response added successfully');
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to add response: ${(error as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {review.admin_response ? 'Edit Response' : 'Add Response'}
            </DialogTitle>
            <DialogDescription>
              Your response will be displayed to users viewing this review.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Original Review:</h3>
              <div className="rounded-md bg-muted p-3">
                <p className="text-sm font-medium">
                  {review.title || `${review.rating}-star review`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {review.content || 'No content provided.'}
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="response" className="text-sm font-medium">
                Your Response:
              </label>
              <Textarea
                id="response"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Enter your response to this review..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Response'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
